import {
  CloseOutlined,
  DownOutlined,
  FilterOutlined,
  InboxOutlined,
} from "@ant-design/icons";
import { Badge, Checkbox, Collapse, Dropdown, Flex, Input, Tag, Tooltip } from "antd";
import React, { useEffect, useMemo, useRef, useState } from "react";
import favoIcon from "../../../assets/Icons/favoIcon.svg";
import searchIcon from "../../../assets/Icons/input-search-icon.svg";
import recentIcon from "../../../assets/Icons/recentIcon.svg";
import starIcon from "../../../assets/Icons/star.svg";

import { useDispatch, useSelector } from "react-redux";
import { clearQuery, setQuery } from "../../../store/filters";
import "../../actionHeader/ActionHeader.scss";
import "../../actionHeader/FilterPanel.scss";

const { Panel } = Collapse;

/* Long filter names are ellipsised by CSS; this adds the full text back as a
   tooltip, but only when the label is actually cut off. Measured on hover so
   nothing is computed for labels nobody looks at. */
const TruncatedText = ({ text, className = "" }) => {
  const ref = useRef(null);
  const [isClipped, setIsClipped] = useState(false);

  const measure = () => {
    const el = ref.current;
    if (el) setIsClipped(el.scrollWidth > el.clientWidth + 1);
  };

  return (
    <Tooltip title={isClipped ? text : ""} mouseEnterDelay={0.35}>
      <span
        ref={ref}
        className={`ce-truncate ${className}`.trim()}
        onMouseEnter={measure}
      >
        {text}
      </span>
    </Tooltip>
  );
};

const FilterDropdown = ({
  setPrimaryItems,
  setSecondaryItems,
  secondaryItems,
  primaryItems,
  fetchData,
  fetchData2,
  fetchData3,
  variant = "1",
  updateApis = () => {},
}) => {
  const [open, setOpen] = useState(false);
  const [screenWidth, setScreenWidth] = useState(window.innerWidth);
  const [selectedCategory, setSelectedCategory] = useState("portfolios");
  const [categorySelections, setCategorySelections] = useState({});
  const [readyToApply, setReadyToApply] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");

  const dispatch = useDispatch();
  const { filter_list, query } = useSelector((state) => state?.filters);

  useEffect(() => {
    const onResize = () => setScreenWidth(window.innerWidth);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  const variant1Categories = [
    { title: "Product", value: "product" }, // pending from backend
    { title: "POS", value: "pos" }, // pending from backend
    { title: "Languages", value: "languages" },
    { title: "Region", value: "region" },
    { title: "Bank", value: "portfolios" },
    { title: "Disposition", value: "disposition" },
    { title: "Aging Bucket", value: "buckets" },
    { title: "Branch", value: "branches" },
    { title: "Supervisor", value: "supervisors" },
    { title: "Team Lead", value: "teamleads" },
    { title: "Tele caller", value: "telecallers" },
    { title: "Field Agent", value: "field_agents" },
  ];

  const variant2Categories = [
    { title: "Recently Selected", value: "recently" },
    { title: "Favourites", value: "favourites" },
    { title: "State Wise", value: "states" },
    { title: "Portfolio Wise", value: "portfolios" },
    { title: "Region Wise", value: "region" },
  ];

  const categories = variant === "2" ? variant2Categories : variant1Categories;

  useEffect(() => {
    if (query && Object.keys(query).length > 0) {
      setCategorySelections(query);
    } else {
      setCategorySelections({});
    }
  }, [query]);

  /* These categories arrive as objects and are stored by id; disposition is
     stored by its code key. Everything else is a plain string. */
  const objectCategories = [
    "portfolios",
    "supervisors",
    "teamleads",
    "telecallers",
    "field_agents",
    "disposition",
  ];

  const valueOf = (category, trust) => {
    if (category === "disposition") return trust?.key ?? trust?.id ?? trust;
    if (objectCategories.includes(category)) return trust?.id ?? trust;
    return trust?.name ?? trust;
  };

  /* Filter items are plain strings for some categories (languages, region,
     buckets) and objects for others (portfolios, telecallers, disposition).
     Always read the displayed label through this. */
  const getFilterLabel = (item) => {
    if (item == null) return "";
    if (typeof item === "string") return item;
    return String(item.name ?? item.key ?? item.email ?? item.id ?? "");
  };

  const toggleTrust = (trust) => {
    const prevSelected = categorySelections[selectedCategory] || [];
    const value = valueOf(selectedCategory, trust);

    const updated = prevSelected.includes(value)
      ? prevSelected.filter((t) => t !== value)
      : [...prevSelected, value];

    setCategorySelections({
      ...categorySelections,
      [selectedCategory]: updated,
    });
  };

  const handleSelectAll = () => {
    // Only what the current search actually shows.
    const visible =
      filteredCategories.find((c) => c.value === selectedCategory)
        ?.filteredFilters || [];

    const values = visible.map((f) => valueOf(selectedCategory, f));
    const existing = categorySelections[selectedCategory] || [];

    setCategorySelections({
      ...categorySelections,
      [selectedCategory]: Array.from(new Set([...existing, ...values])),
    });
  };

  const clearAll = () => {
    setCategorySelections({});
    dispatch(clearQuery());
  };

  const applyFilters = () => {
    setOpen(false);
    setReadyToApply(true);
    dispatch(setQuery(categorySelections));
    updateApis(categorySelections);
    if (typeof fetchData === "function") {
      dispatch(fetchData(categorySelections)).then(() =>
        setReadyToApply(false),
      );
    }
    if (typeof fetchData2 === "function") {
      dispatch(fetchData2(categorySelections));
    }
    if (typeof fetchData3 === "function") {
      dispatch(fetchData3(categorySelections));
    }
  };

  const toggleFavorite = (trust) => {
    // Implement favorite logic if needed
  };

  const handleSelectCategory = (cat) => {
    setSelectedCategory(cat?.value);
  };

  /* The old search called filter.toLowerCase() on every item, which threw as
     soon as a category held objects (portfolios, telecallers, disposition).
     Labels are resolved first now, so search works across all of them. */
  const filteredCategories = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();

    const withFilters = categories.map((cat) => ({
      ...cat,
      filteredFilters: (
        filter_list?.find((f) => f.name === cat.value)?.filters || []
      ).filter(
        (item) => !term || getFilterLabel(item).toLowerCase().includes(term),
      ),
    }));

    // While searching, hide categories that have nothing to show.
    return term
      ? withFilters.filter((cat) => cat.filteredFilters.length > 0)
      : withFilters;
  }, [categories, filter_list, searchTerm]);

  /* Keep the rail selection valid: if a search hides the active category,
     fall through to the first one that still has matches. */
  useEffect(() => {
    if (!filteredCategories.length) return;
    const stillVisible = filteredCategories.some(
      (c) => c.value === selectedCategory,
    );
    if (!stillVisible) {
      setSelectedCategory(filteredCategories[0].value);
    }
  }, [filteredCategories, selectedCategory]);

  /* Count only list-style selections; scalar query keys (portfolio, intensity)
     are set by other controls and are not editable from this panel. */
  const countSelections = (source) =>
    Object.values(source || {}).reduce(
      (n, v) => n + (Array.isArray(v) ? v.length : 0),
      0,
    );

  const selectedCount = useMemo(
    () => countSelections(categorySelections),
    [categorySelections],
  );

  const appliedCount = useMemo(() => countSelections(query), [query]);

  const activeCategoryCount = useMemo(
    () =>
      Object.values(categorySelections || {}).filter(
        (v) => Array.isArray(v) && v.length > 0,
      ).length,
    [categorySelections],
  );

  const activeCategory = filteredCategories.find(
    (c) => c.value === selectedCategory,
  );
  const activeFilters = activeCategory?.filteredFilters || [];

  const isTrustChecked = (trust) =>
    (categorySelections[selectedCategory] || []).includes(
      valueOf(selectedCategory, trust),
    );

  const categoryTitle = (value) =>
    categories.find((c) => c.value === value)?.title || value;

  const selectedChips = Object.entries(categorySelections).flatMap(
    ([cat, selected]) => {
      if (!Array.isArray(selected)) return [];
      return selected.map((value) => {
        let displayName = value;
        if (objectCategories.includes(cat)) {
          const match = filter_list
            ?.find((f) => f.name === cat)
            ?.filters?.find(
              (f) => (cat === "disposition" ? f.key ?? f.id : f.id) === value,
            );
          displayName = match ? getFilterLabel(match) : value;
        }
        return { cat, value, displayName };
      });
    },
  );

  const removeChip = (cat, value) => {
    setCategorySelections({
      ...categorySelections,
      [cat]: (categorySelections[cat] || []).filter((t) => t !== value),
    });
  };

  const dropdownContent = (
    <div className="ce-filter">
      <div className="ce-filter__head">
        <div className="ce-filter__title">
          <span className="ce-filter__icon">
            <FilterOutlined />
          </span>
          <div>
            <h2>Filters</h2>
            <p>
              {selectedCount > 0
                ? `${selectedCount} selected across ${activeCategoryCount} ${
                    activeCategoryCount === 1 ? "category" : "categories"
                  }`
                : "Narrow the cases down to what you need"}
            </p>
          </div>
        </div>
        {/* Actions live in the head so they are reachable without
            scrolling past the options. */}
        <div className="ce-filter__head-actions">
          <button type="button" className="ce-filter__ghost" onClick={clearAll}>
            Clear all
          </button>
          <button
            type="button"
            className="ce-filter__apply"
            onClick={applyFilters}
          >
            Apply filters
          </button>
          <button
            type="button"
            className="ce-filter__close"
            aria-label="Close filters"
            onClick={() => setOpen(false)}
          >
            <CloseOutlined />
          </button>
        </div>
      </div>

      <div className="ce-filter__search">
        <Input
          prefix={<img src={searchIcon} alt="" />}
          placeholder="Search across every filter"
          allowClear
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
      </div>

      <div className="ce-filter__body">
        <div className="ce-filter__col ce-filter__col--cats">
          <div className="ce-filter__col-head">Category</div>
          <div className="ce-filter__cats">
            {filteredCategories.length === 0 && (
              <div className="ce-filter__empty">
                <InboxOutlined />
                <span>No category matches "{searchTerm}"</span>
              </div>
            )}
            {filteredCategories?.map((cat, index) => {
              const count = (categorySelections[cat?.value] || []).length;
              return (
                <React.Fragment key={index}>
                  <button
                    type="button"
                    className={`ce-filter__cat ${
                      selectedCategory === cat?.value ? "is-active" : ""
                    }`}
                    onClick={() => handleSelectCategory(cat)}
                  >
                    {cat.title === "Recently Selected" ? (
                      <img src={recentIcon} alt="" />
                    ) : cat.title === "Favourites" ? (
                      <img src={favoIcon} alt="" />
                    ) : null}
                    <TruncatedText text={cat?.title} />
                    {count > 0 && (
                      <i className="ce-filter__cat-count">{count}</i>
                    )}
                  </button>
                  {cat.title === "Favourites" && (
                    <div className="ce-filter__divider" />
                  )}
                </React.Fragment>
              );
            })}
          </div>
        </div>

        <div className="ce-filter__col ce-filter__col--opts">
          <div className="ce-filter__col-head">
            <TruncatedText text={activeCategory?.title || "Options"} />
            {activeFilters.length > 0 && (
              <button
                type="button"
                className="ce-filter__link"
                onClick={handleSelectAll}
              >
                Select all
              </button>
            )}
          </div>
          <div className="ce-filter__opts">
            {activeFilters.length > 0 ? (
              activeFilters.map((trust) => (
                <div
                  key={trust?.id ?? trust?.key ?? getFilterLabel(trust)}
                  className="ce-filter__opt"
                >
                  <Checkbox
                    checked={isTrustChecked(trust)}
                    onChange={() => toggleTrust(trust)}
                  >
                    <TruncatedText text={getFilterLabel(trust)} />
                  </Checkbox>
                  <span
                    className="ce-filter__star"
                    onClick={() => toggleFavorite(trust)}
                  >
                    <img src={starIcon} alt="favourite" />
                  </span>
                </div>
              ))
            ) : (
              <div className="ce-filter__empty">
                <InboxOutlined />
                <span>
                  {searchTerm
                    ? "No filters match your search"
                    : "Nothing to filter in this category yet"}
                </span>
              </div>
            )}
          </div>
        </div>

        <div className="ce-filter__col ce-filter__col--sel">
          <div className="ce-filter__col-head">
            <span>Selected</span>
            {selectedCount > 0 && (
              <span className="ce-filter__count">{selectedCount}</span>
            )}
          </div>
          <div className="ce-filter__chips">
            {selectedChips.length > 0 ? (
              selectedChips.map(({ cat, value, displayName }) => (
                <Tag
                  key={cat + value}
                  closable
                  onClose={() => removeChip(cat, value)}
                  closeIcon={<CloseOutlined />}
                >
                  <TruncatedText text={displayName} />
                </Tag>
              ))
            ) : (
              <div className="ce-filter__empty">
                <InboxOutlined />
                <span>Nothing selected yet</span>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );

  const mobileMenuDropdownContent = (
    <div className="ce-filter ce-filter--mobile">
      <div className="ce-filter__head">
        <div className="ce-filter__title">
          <span className="ce-filter__icon">
            <FilterOutlined />
          </span>
          <div>
            <h2>Filters</h2>
            <p>
              {selectedCount > 0
                ? `${selectedCount} selected`
                : "Narrow the cases down"}
            </p>
          </div>
        </div>
        <div className="ce-filter__head-actions">
          <button type="button" className="ce-filter__ghost" onClick={clearAll}>
            Clear all
          </button>
          <button
            type="button"
            className="ce-filter__apply"
            onClick={applyFilters}
          >
            Apply
          </button>
          <button
            type="button"
            className="ce-filter__close"
            aria-label="Close filters"
            onClick={() => setOpen(false)}
          >
            <CloseOutlined />
          </button>
        </div>
      </div>

      <div className="ce-filter__body">
        <Collapse defaultActiveKey={["1"]} bordered={false}>
          {filteredCategories?.map((cat, index) => {
            const currentFilter = cat.filteredFilters;
            const currentSelections = categorySelections[cat.value] || [];
            return (
              <Panel
                header={
                  <Flex gap={2}>
                    <Badge count={currentSelections?.length} offset={[10, 1]}>
                      <h2>{cat?.title}</h2>
                    </Badge>
                  </Flex>
                }
                key={index + 1}
                className="filter-panel"
              >
                <div className="ce-filter__col-head">
                  <span>Options</span>
                  {currentFilter.length > 0 && (
                    <button
                      type="button"
                      className="ce-filter__link"
                      onClick={() => {
                        const values = currentFilter.map((f) =>
                          valueOf(cat.value, f),
                        );
                        setCategorySelections({
                          ...categorySelections,
                          [cat.value]: Array.from(
                            new Set([...currentSelections, ...values]),
                          ),
                        });
                      }}
                    >
                      Select all
                    </button>
                  )}
                </div>
                <div className="ce-filter__opts">
                  {currentFilter.length > 0 ? (
                    currentFilter.map((trust) => {
                      const value = valueOf(cat.value, trust);
                      return (
                        <div
                          key={trust?.id ?? trust?.key ?? getFilterLabel(trust)}
                          className="ce-filter__opt"
                        >
                          <Checkbox
                            checked={currentSelections.includes(value)}
                            onChange={() => {
                              const updated = currentSelections.includes(value)
                                ? currentSelections.filter((t) => t !== value)
                                : [...currentSelections, value];
                              setCategorySelections({
                                ...categorySelections,
                                [cat.value]: updated,
                              });
                            }}
                          >
                            <TruncatedText text={getFilterLabel(trust)} />
                          </Checkbox>
                          <span
                            className="ce-filter__star"
                            onClick={() => toggleFavorite(trust)}
                          >
                            <img src={starIcon} alt="favourite" />
                          </span>
                        </div>
                      );
                    })
                  ) : (
                    <div className="ce-filter__empty">
                      <InboxOutlined />
                      <span>No options in this category</span>
                    </div>
                  )}
                </div>
              </Panel>
            );
          })}
        </Collapse>
      </div>
    </div>
  );

  /* Not memoised on purpose: the panel also reacts to selectedCategory, which
     a dependency list omitting it would freeze. Both elements are built on
     every render anyway, so there is nothing to save. */
  const isDropdown =
    screenWidth > 800 ? dropdownContent : mobileMenuDropdownContent;

  return (
    <Dropdown
      open={open}
      onOpenChange={setOpen}
      dropdownRender={() => isDropdown}
      placement="bottomRight"
      trigger={["click"]}
    >
      <button
        type="button"
        className={[
          "ce-filter-trigger",
          appliedCount > 0 ? "is-active" : "",
          open ? "is-open" : "",
        ]
          .filter(Boolean)
          .join(" ")}
        aria-label="Open filters"
      >
        <FilterOutlined />
        <span>Filters</span>
        {appliedCount > 0 && (
          <i className="ce-filter-trigger__badge">{appliedCount}</i>
        )}
        <DownOutlined className="ce-caret" />
      </button>
    </Dropdown>
  );
};

export default FilterDropdown;
